// Lib
import React, { useMemo } from 'react';

// Components
import CartProductQtyButtons from './CartProductQtyButtons/CartProductQtyButtons';
import KitsProductQtyButtons from '../Kits/KitsProductQtyButtons/KitsProductQtyButtons';
import KitProductQty from '../Kits/KitProductQty/KitProductQty';

// Hooks
import useUser from '../../hooks/useUser';
import useUI from '../../hooks/useUI';

// Utils
import getRemainingQuantity from '../../utils/getRemainigQuantity';
import isDigitalAsset from '../../utils/isDigitalAsset';
import { getLimits } from '../../utils/overLimit';
import { getAddressesCount } from '../../pages/OrderHistory/utils/getQuantity';
import { pickIf } from '../../utils/pickIf';

// Constants
import { DOWNLOAD_ONLY_LABEL } from '../../constants/products';

// Styles
import { useStyles } from './DetailsInfoStyles';

const DetailsInfo = ({
  product,
  cartProps,
  orderedProduct,
  pendingApproval,
  kit,
  nums,
  isOverLimit,
  placedOrder,
  isUnavailableForOrder,
  unavailableLabel = false,
  shippingAddresses = 1,
  readOnly = false,
  KitQtyComponent = KitsProductQtyButtons,
}) => {
  const { isTabletMode, isMobileMode } = useUI();
  const { isKiteUser } = useUser();
  const addressesCount = placedOrder ? getAddressesCount(placedOrder) : 1;

  const {
    brand_category,
    product_id: stockNumber,
    pi_required,
    unit_measure,
    product_available_qty,
    prepack_quantity,
    order_limit,
    limits,
  } = product || {};

  const { classes, cx } = useStyles({
    orderedProduct,
    isKiteUser,
    placedOrder: !!placedOrder?.id,
    hasOrderLimit: order_limit !== null,
  });

  const stockLabel = pickIf(
    isDigitalAsset(product),
    DOWNLOAD_ONLY_LABEL,
    `In Stock: ${product_available_qty}`,
  );

  const { monthLimit } = useMemo(() => getLimits(limits), [limits]);

  const remainingMonthQuantity = useMemo(
    () => getRemainingQuantity(monthLimit),
    [monthLimit],
  );

  const renderAvailabilityLabel = () => {
    return !isUnavailableForOrder ? (
      <div
        className={cx(classes.detailsItem, classes.stockItem)}
        data-testid="product-card-available-qty">
        <span>{stockLabel}</span>
      </div>
    ) : (
      <span className={classes.accentBold}>{unavailableLabel}</span>
    );
  };
  const renderRemainMonthQty = () => {
    return (
      remainingMonthQuantity !== null &&
      remainingMonthQuantity >= 0 && (
        <span>{`${remainingMonthQuantity} Left In Month`}</span>
      )
    );
  };
  const renderKitQty = () => {
    return (
      !orderedProduct &&
      !placedOrder?.id &&
      kit &&
      !isUnavailableForOrder &&
      (readOnly ? (
        <KitProductQty nums={nums} className={classes.qtyButtons} />
      ) : (
        <KitQtyComponent
          product={product}
          isMobileMode={isMobileMode}
          nums={nums}
          detailsMode={false}
          listView={true}
          className={classes.qtyButtons}
        />
      ))
    );
  };
  const renderCartProductQtyButtons = () => {
    return (
      !orderedProduct &&
      !placedOrder?.id &&
      !kit && (
        <CartProductQtyButtons
          isOverLimit={isOverLimit}
          shippingAddresses={shippingAddresses}
          product={product}
          onAdd={cartProps.addOne(product)}
          onRemove={cartProps.removeOne(product)}
          onSetCount={cartProps.setCount}
          count={cartProps.count}
          isMobileMode={isMobileMode}
          detailsMode={false}
          className={classes.qtyButtons}
        />
      )
    );
  };
  if (!isTabletMode) {
    const orderedQty = orderedProduct?.qty_ordered * addressesCount;
    const totalOrderedQty =
      orderedProduct?.qty_ordered * prepack_quantity * addressesCount;

    return (
      <div className={classes.container}>
        <div className={classes.details}>
          <div
            className={classes.detailsItem}
            data-testid="product-card-stock-number">
            <span>Stock Number:</span>
            <span>{stockNumber}</span>
          </div>
          <div
            className={classes.detailsItem}
            data-testid="product-card-brand-category">
            <span>Brand Category:</span>
            <span>{brand_category}</span>
          </div>
        </div>

        <div className={classes.details}>
          <div
            className={classes.detailsItem}
            data-testid="product-card-unit-measure">
            <span>Unit Measure:</span>
            <span>{unit_measure}</span>
          </div>
          <div
            className={classes.detailsItem}
            data-testid="product-card-pi-required">
            <span>PI Required:</span>
            <span>{pickIf(pi_required, 'Yes', 'No')}</span>
          </div>
        </div>

        <div className={classes.details}>
          <div className={classes.detailsItem}>
            <div
              className={classes.detailsItem}
              data-testid="product-card-per-unit-qty">
              <span className={classes.inline}>Per Unit Qty: </span>
              <span className={classes.inline}>{prepack_quantity}</span>
            </div>
            <div
              className={cx(classes.detailsItem, classes.limitRow)}
              data-testid="product-card-max-allowed-qty">
              <span className={classes.inline}>Max. Allowed Qty: </span>
              <span className={classes.inline}>{order_limit}</span>
            </div>
          </div>
          <div>
            {renderAvailabilityLabel()}
            <div
              className={classes.detailsItem}
              data-testid="product-card-left-in-month">
              {renderRemainMonthQty()}
            </div>
          </div>
        </div>
        {orderedProduct && (
          <div className={classes.details}>
            <div className={classes.detailsItemColored}>
              <div data-testid="product-card-order-qty">{`Order Qty${pickIf(
                pendingApproval,
                ' (Requested)',
                '',
              )}: ${orderedQty}`}</div>
              <div
                className={classes.totalItems}
                data-testid="product-card-total-ordered">{`Total Ordered: ${totalOrderedQty}`}</div>
            </div>
          </div>
        )}
        <div className={classes.details}>
          {renderKitQty()}
          {renderCartProductQtyButtons()}
        </div>
      </div>
    );
  }
  const renderAvailableForOrder = () => {
    return !isUnavailableForOrder ? (
      <div
        className={classes.detailsItem}
        data-testid="product-card-available-qty">
        <span>{stockLabel}</span>
      </div>
    ) : (
      <span className={classes.accentBold}>{unavailableLabel}</span>
    );
  };
  const renderOrderedQty = (orderedQty) => {
    return (
      orderedProduct && (
        <div className={cx(classes.detailsItem, classes.detailsItemColored)}>
          <span data-testid="product-card-order-qty">{`Order Qty: ${orderedQty}`}</span>
          {pendingApproval && (
            <div className={classes.approvalInfo}>Pending Approval</div>
          )}
        </div>
      )
    );
  };
  if (isTabletMode && !isMobileMode) {
    const orderedQty = orderedProduct?.qty_ordered * addressesCount;
    const totalOrderedQty = orderedProduct?.qty_ordered * prepack_quantity;

    return (
      <div className={classes.container}>
        <div className={classes.details}>
          <div
            className={classes.detailsItem}
            data-testid="product-card-stock-number">
            <span>Stock Number:</span>
            <span>{stockNumber}</span>
          </div>
          <div
            className={classes.detailsItem}
            data-testid="product-card-brand-category">
            <span>Brand Category:</span>
            <span>{brand_category}</span>
          </div>
          <div
            className={classes.detailsItem}
            data-testid="product-card-unit-measure">
            <span>Unit Measure:</span>
            <span>{unit_measure}</span>
          </div>
          <div
            className={classes.detailsItem}
            data-testid="product-card-pi-required">
            <span>PI Required:</span>
            <span>{pickIf(pi_required, 'Yes', 'No')}</span>
          </div>
          {renderOrderedQty(orderedQty)}
        </div>
        <div className={classes.details}>
          <div className={classes.column}>
            <div
              className={classes.detailsItem}
              data-testid="product-card-per-unit-qty">
              <span>Per Unit Qty: </span>
              <span>{prepack_quantity}</span>
            </div>
            <div
              className={cx(classes.detailsItem, classes.limitRowPlaced)}
              data-testid="product-card-max-allowed-qty">
              <span>Max. Allowed Qty: </span>
              <span>{order_limit}</span>
            </div>
          </div>
          <div className={classes.column}>
            {renderAvailableForOrder()}
            {renderRemainMonthQty()}
          </div>
          {orderedProduct && (
            <div
              className={cx(
                classes.detailsItem,
                classes.detailsItemColored,
                classes.orderedProduct,
              )}>
              <span
                className={classes.totalItems}
                data-testid="product-card-total-ordered">{`Total Ordered: ${totalOrderedQty}`}</span>
            </div>
          )}
        </div>
      </div>
    );
  }
  if (isMobileMode) {
    return <></>;
  }
};

export default DetailsInfo;
